import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { Plus, Trash2, FileText, Users, ArrowRight, X, Search, Loader2 } from 'lucide-react';
import { formatDate } from '../lib/utils';

const LeadGroups = () => {
  const [groups, setGroups] = useState([]);
  const [callerGroups, setCallerGroups] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddLeadsModal, setShowAddLeadsModal] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedCallerGroup, setSelectedCallerGroup] = useState('');
  const [distributionType, setDistributionType] = useState('random');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroup, setExpandedGroup] = useState(null);
  // Loading states for async operations
  const [isCreating, setIsCreating] = useState(false);
  const [isAddingLeads, setIsAddingLeads] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const user = authService.getCurrentUser();
  const token = authService.getToken();

  useEffect(() => {
    if (!user || !token) return;
    if (!['admin', 'manager'].includes(user?.role)) return;
    
    // Small delay to ensure auth is ready
    const timer = setTimeout(() => {
      fetchGroups();
      fetchCallerGroups();
      fetchLeads();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [user, token]);

  const fetchGroups = async () => {
    try {
      const response = await api.getLeadGroups();
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch lead groups:', error);
      // Only show error toast for non-auth errors
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        toast.error('Failed to fetch lead groups');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCallerGroups = async () => {
    try {
      const response = await api.getCallerGroups();
      setCallerGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch caller groups:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      // Fetch leads with a large limit to get all available leads for selection
      const response = await api.getLeads({ limit: 10000 });
      // Handle paginated response - extract the leads array
      const leadsData = response.data.leads || response.data;
      setLeads(Array.isArray(leadsData) ? leadsData : []);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    setIsCreating(true);
    try {
      await api.createLeadGroup(newGroupName, newGroupDesc);
      toast.success('Lead group created successfully');
      setNewGroupName('');
      setNewGroupDesc('');
      setShowCreateModal(false);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create group');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this lead group?')) return;

    setIsDeleting(groupId);
    try {
      await api.deleteLeadGroup(groupId);
      toast.success('Lead group deleted');
      fetchGroups();
    } catch (error) {
      toast.error('Failed to delete group');
    } finally {
      setIsDeleting(null);
    }
  };

  const handleAddLeads = async (skipDuplicateCheck = false) => {
    if (!selectedLeads.length) {
      toast.error('Please select at least one lead');
      return;
    }

    // First check for duplicates (unless we're confirming after seeing duplicates)
    if (!skipDuplicateCheck) {
      setIsAddingLeads(true);
      try {
        const checkResponse = await api.checkDuplicatesInGroup(showAddLeadsModal, selectedLeads);
        const checkData = checkResponse.data;
        
        if (checkData.has_duplicates) {
          // Show confirmation dialog
          setDuplicateCheck(checkData);
          setShowDuplicateConfirm(true);
          setIsAddingLeads(false);
          return;
        }
      } catch (error) {
        console.error('Failed to check duplicates:', error);
        // Continue with adding if check fails
      }
    }

    setIsAddingLeads(true);
    try {
      const response = await api.addLeadsToGroup(showAddLeadsModal, selectedLeads);
      const data = response.data;
      
      // Show detailed message
      if (data.duplicate_leads_skipped > 0) {
        toast.warning(`${data.new_leads_added} new lead(s) added. ${data.duplicate_leads_skipped} duplicate(s) were skipped.`);
      } else {
        toast.success(`${data.new_leads_added} lead(s) added to group`);
      }
      
      setShowAddLeadsModal(null);
      setSelectedLeads([]);
      setSearchQuery('');
      setDuplicateCheck(null);
      setShowDuplicateConfirm(false);
      fetchGroups();
      fetchLeads();
    } catch (error) {
      toast.error('Failed to add leads');
    } finally {
      setIsAddingLeads(false);
    }
  };

  const handleConfirmAddWithDuplicates = () => {
    setShowDuplicateConfirm(false);
    handleAddLeads(true); // Skip duplicate check since we're confirming
  };

  const handleCancelDuplicateAdd = () => {
    setShowDuplicateConfirm(false);
    setDuplicateCheck(null);
  };

  const handleAssignToCallerGroup = async () => {
    if (!selectedCallerGroup) {
      toast.error('Please select a caller group');
      return;
    }

    setIsAssigning(true);
    try {
      const response = await api.assignLeadGroupToCallerGroup(
        showAssignModal,
        selectedCallerGroup,
        distributionType
      );
      
      toast.success(response.data.message);
      setShowAssignModal(null);
      setSelectedCallerGroup('');
      setDistributionType('random');
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign leads');
    } finally {
      setIsAssigning(false);
    }
  };

  const getAvailableLeads = (group) => {
    const groupLeadIds = new Set(group?.lead_ids || []);
    // Filter to show only freshly imported (unassigned) leads that are not already in this group
    return leads.filter(l => 
      !groupLeadIds.has(l.id) && 
      (!l.assigned_to || l.assigned_to === null || l.assigned_to === '')
    );
  };

  // Get all unassigned leads (not in any group context)
  const getAllUnassignedLeads = () => {
    return leads.filter(l => 
      !l.assigned_to || l.assigned_to === null || l.assigned_to === ''
    );
  };

  const filteredLeads = (group) => {
    const available = getAvailableLeads(group);
    if (!searchQuery) return available.slice(0, 100);
    
    const query = searchQuery.toLowerCase();
    return available.filter(l => 
      l.name?.toLowerCase().includes(query) ||
      l.phone?.includes(query) ||
      l.course?.toLowerCase().includes(query)
    ).slice(0, 100);
  };

  const unassignedLeadsCount = getAllUnassignedLeads().length;

  if (!['admin', 'manager'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <FileText size={48} className="mx-auto text-slate-300 mb-4" />
        <p className="text-slate-600 font-body">You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600 font-body">Loading lead groups...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-headings uppercase tracking-tight text-slate-900">
            Lead Groups
          </h1>
          <p className="text-slate-600 font-body mt-1">
            Create and manage groups of leads for bulk assignment to caller groups
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          data-testid="create-lead-group-btn"
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-all shadow-sm font-body"
        >
          <Plus size={18} />
          <span>Create Group</span>
        </button>
      </div>

      {/* Unassigned Leads Alert Banner */}
      {unassignedLeadsCount > 0 && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Users size={24} className="text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-amber-800 font-body">
                {unassignedLeadsCount} Unassigned Lead{unassignedLeadsCount !== 1 ? 's' : ''}
              </p>
              <p className="text-sm text-amber-700 font-body">
                These leads have not been assigned to any caller yet
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            data-testid="create-group-for-unassigned-btn"
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-all shadow-sm font-body"
          >
            <Plus size={18} />
            <span>Create Group with Unassigned</span>
          </button>
        </div>
      )}

      {/* Groups List */}
      {groups.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
          <FileText size={64} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-600 font-body text-lg">No lead groups yet</p>
          <p className="text-slate-500 font-body text-sm mt-2">
            Create a group to organize your leads for bulk assignment
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <div
              key={group.id}
              data-testid={`lead-group-${group.id}`}
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
            >
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                onClick={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-emerald-50">
                    <FileText size={24} className="text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 font-body">{group.name}</h3>
                    <p className="text-sm text-slate-500 font-body">
                      {group.lead_count} lead(s) • Created {formatDate(group.created_at)}
                    </p>
                    {group.description && (
                      <p className="text-sm text-slate-600 font-body mt-1">{group.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAddLeadsModal(group.id);
                    }}
                    data-testid={`add-leads-${group.id}`}
                    className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors"
                    title="Add Leads"
                  >
                    <Plus size={18} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAssignModal(group.id);
                    }}
                    data-testid={`assign-group-${group.id}`}
                    className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm font-body"
                    title="Assign to Caller Group"
                  >
                    <Users size={14} />
                    <span>Assign</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteGroup(group.id);
                    }}
                    disabled={isDeleting === group.id}
                    data-testid={`delete-lead-group-${group.id}`}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                    title="Delete Group"
                  >
                    {isDeleting === group.id ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                  </button>
                </div>
              </div>

              {/* Expanded Leads Preview */}
              {expandedGroup === group.id && (
                <div className="border-t border-slate-200 p-4 bg-slate-50">
                  <h4 className="text-sm font-semibold text-slate-700 font-body mb-3">
                    Leads in Group (showing first 10)
                  </h4>
                  {group.lead_count === 0 ? (
                    <p className="text-sm text-slate-500 font-body">No leads in this group yet</p>
                  ) : (
                    <div className="space-y-2">
                      {leads
                        .filter(l => group.lead_ids?.includes(l.id))
                        .slice(0, 10)
                        .map((lead) => (
                          <div
                            key={lead.id}
                            className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200"
                          >
                            <div>
                              <p className="font-medium text-slate-900 font-body text-sm">{lead.name}</p>
                              <p className="text-xs text-slate-500 font-mono">{lead.phone}</p>
                            </div>
                            <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded-full ${
                              lead.status === 'hot' ? 'bg-red-100 text-red-700' :
                              lead.status === 'warm' ? 'bg-amber-100 text-amber-700' :
                              lead.status === 'cold' ? 'bg-slate-100 text-slate-600' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {lead.status}
                            </span>
                          </div>
                        ))}
                      {group.lead_count > 10 && (
                        <p className="text-sm text-slate-500 font-body text-center mt-2">
                          + {group.lead_count - 10} more leads
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Group Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold font-headings text-slate-900">Create Lead Group</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateGroup} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 font-body mb-1">Group Name</label>
                <input
                  type="text"
                  data-testid="lead-group-name-input"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="e.g., January Batch, Delhi Region"
                  className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 font-body mb-1">Description (Optional)</label>
                <input
                  type="text"
                  data-testid="lead-group-desc-input"
                  value={newGroupDesc}
                  onChange={(e) => setNewGroupDesc(e.target.value)}
                  placeholder="Brief description of this group"
                  className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={isCreating}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-md hover:bg-slate-50 font-body disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  data-testid="submit-create-lead-group"
                  className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 font-body disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isCreating && <Loader2 size={16} className="animate-spin" />}
                  {isCreating ? 'Creating...' : 'Create Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Leads Modal */}
      {showAddLeadsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold font-headings text-slate-900">Add Leads to Group</h2>
                <p className="text-sm text-slate-500 font-body mt-1">Showing freshly imported (unassigned) leads only</p>
              </div>
              <button onClick={() => { setShowAddLeadsModal(null); setSelectedLeads([]); setSearchQuery(''); }} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search leads by name, phone, or course..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
              />
            </div>

            {/* Select All / Deselect All */}
            {filteredLeads(groups.find(g => g.id === showAddLeadsModal)).length > 0 && (
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-200">
                <span className="text-sm text-slate-600 font-body">
                  {selectedLeads.length} lead(s) selected
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const allLeadIds = filteredLeads(groups.find(g => g.id === showAddLeadsModal)).map(l => l.id);
                      setSelectedLeads(allLeadIds);
                    }}
                    data-testid="select-all-leads"
                    className="px-3 py-1 text-sm bg-emerald-100 text-emerald-700 rounded-md hover:bg-emerald-200 font-body transition-colors"
                  >
                    Select All ({filteredLeads(groups.find(g => g.id === showAddLeadsModal)).length})
                  </button>
                  {selectedLeads.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedLeads([])}
                      data-testid="deselect-all-leads"
                      className="px-3 py-1 text-sm bg-slate-100 text-slate-600 rounded-md hover:bg-slate-200 font-body transition-colors"
                    >
                      Deselect All
                    </button>
                  )}
                </div>
              </div>
            )}
            
            <div className="flex-1 overflow-y-auto space-y-2 mb-4">
              {filteredLeads(groups.find(g => g.id === showAddLeadsModal)).length === 0 ? (
                <p className="text-slate-500 font-body text-center py-4">No leads available</p>
              ) : (
                filteredLeads(groups.find(g => g.id === showAddLeadsModal)).map((lead) => (
                  <label
                    key={lead.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedLeads.includes(lead.id)
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedLeads.includes(lead.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLeads([...selectedLeads, lead.id]);
                        } else {
                          setSelectedLeads(selectedLeads.filter(id => id !== lead.id));
                        }
                      }}
                      className="w-4 h-4 text-emerald-600 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900 font-body">{lead.name}</p>
                        <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded-full ${
                          lead.status === 'hot' ? 'bg-red-100 text-red-700' :
                          lead.status === 'warm' ? 'bg-amber-100 text-amber-700' :
                          lead.status === 'cold' ? 'bg-slate-100 text-slate-600' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {lead.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 font-mono">{lead.phone}</p>
                      {lead.course && <p className="text-xs text-slate-400 font-body">{lead.course}</p>}
                    </div>
                  </label>
                ))
              )}
            </div>

            <div className="flex gap-3 pt-2 border-t border-slate-200">
              <button
                onClick={() => { setShowAddLeadsModal(null); setSelectedLeads([]); setSearchQuery(''); }}
                disabled={isAddingLeads}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-md hover:bg-slate-50 font-body disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLeads}
                disabled={selectedLeads.length === 0 || isAddingLeads}
                data-testid="confirm-add-leads"
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 font-body disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isAddingLeads && <Loader2 size={16} className="animate-spin" />}
                {isAddingLeads ? 'Adding...' : `Add ${selectedLeads.length} Lead(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign to Caller Group Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold font-headings text-slate-900">Assign to Caller Group</h2>
              <button onClick={() => { setShowAssignModal(null); setSelectedCallerGroup(''); }} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600 font-body">
                  <strong>Lead Group:</strong> {groups.find(g => g.id === showAssignModal)?.name}
                </p>
                <p className="text-sm text-slate-600 font-body">
                  <strong>Leads:</strong> {groups.find(g => g.id === showAssignModal)?.lead_count}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 font-body mb-2">Select Caller Group</label>
                {callerGroups.length === 0 ? (
                  <p className="text-sm text-slate-500 font-body p-3 bg-slate-50 rounded-lg">
                    No caller groups available. Create a caller group first.
                  </p>
                ) : (
                  <select
                    value={selectedCallerGroup}
                    onChange={(e) => setSelectedCallerGroup(e.target.value)}
                    data-testid="select-caller-group"
                    className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
                  >
                    <option value="">-- Select Caller Group --</option>
                    {callerGroups.map((cg) => (
                      <option key={cg.id} value={cg.id}>
                        {cg.name} ({cg.caller_count} callers)
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 font-body mb-2">Distribution Type</label>
                <div className="flex gap-3">
                  <label className={`flex-1 p-3 rounded-lg border cursor-pointer transition-colors ${
                    distributionType === 'random' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'
                  }`}>
                    <input
                      type="radio"
                      name="distribution"
                      value="random"
                      checked={distributionType === 'random'}
                      onChange={(e) => setDistributionType(e.target.value)}
                      className="sr-only"
                    />
                    <p className="font-medium text-slate-900 font-body text-sm">Random</p>
                    <p className="text-xs text-slate-500 font-body">Leads randomly distributed</p>
                  </label>
                  <label className={`flex-1 p-3 rounded-lg border cursor-pointer transition-colors ${
                    distributionType === 'equal' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'
                  }`}>
                    <input
                      type="radio"
                      name="distribution"
                      value="equal"
                      checked={distributionType === 'equal'}
                      onChange={(e) => setDistributionType(e.target.value)}
                      className="sr-only"
                    />
                    <p className="font-medium text-slate-900 font-body text-sm">Equal</p>
                    <p className="text-xs text-slate-500 font-body">Equal leads per caller</p>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { setShowAssignModal(null); setSelectedCallerGroup(''); }}
                  disabled={isAssigning}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-md hover:bg-slate-50 font-body disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignToCallerGroup}
                  disabled={!selectedCallerGroup || isAssigning}
                  data-testid="confirm-assign-group"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-body disabled:opacity-50"
                >
                  {isAssigning ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Assigning...</span>
                    </>
                  ) : (
                    <>
                      <span>Assign Leads</span>
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Confirmation Modal */}
      {showDuplicateConfirm && duplicateCheck && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-amber-100">
                <FileText size={24} className="text-amber-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold font-headings text-slate-900">Duplicate Leads Found</h2>
                <p className="text-sm text-slate-500 font-body">Some leads are already in this group</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800 font-body">
                  <strong>{duplicateCheck.duplicate_leads_count}</strong> lead(s) are already present in this group.
                </p>
                <p className="text-sm text-amber-700 font-body mt-1">
                  Only <strong>{duplicateCheck.new_leads_count}</strong> new lead(s) will be added.
                </p>
              </div>

              {duplicateCheck.duplicate_leads.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 font-body mb-2">
                    Existing leads (showing first {Math.min(10, duplicateCheck.duplicate_leads.length)}):
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {duplicateCheck.duplicate_leads.map((lead) => (
                      <div key={lead.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                        <span className="font-medium text-slate-700">{lead.name}</span>
                        <span className="text-slate-500 font-mono text-xs">{lead.phone}</span>
                      </div>
                    ))}
                  </div>
                  {duplicateCheck.duplicate_leads_count > 10 && (
                    <p className="text-xs text-slate-500 mt-1 font-body">
                      + {duplicateCheck.duplicate_leads_count - 10} more existing leads
                    </p>
                  )}
                </div>
              )}

              <p className="text-sm text-slate-600 font-body">
                <strong>Note:</strong> The status of existing leads will NOT be changed. Only new leads will be added to the group.
              </p>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleCancelDuplicateAdd}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-md hover:bg-slate-50 font-body"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmAddWithDuplicates}
                  disabled={duplicateCheck.new_leads_count === 0 || isAddingLeads}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 font-body disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isAddingLeads && <Loader2 size={16} className="animate-spin" />}
                  {duplicateCheck.new_leads_count === 0 
                    ? 'No New Leads to Add' 
                    : `Add ${duplicateCheck.new_leads_count} New Lead(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadGroups;
